
import React, { useState, useEffect } from 'react';
import { Row, Button, Spin, Pagination, Modal, notification, Table as AntTable } from 'antd';
import "../../css/poolingHistory.css";
import {
  poolingHistory,
  poolingHistoryOpenDetails,
  deletePool,
  fetchCompanySummary,
} from "../../features/Pooling/PoolingSlice";
import dayjs from "dayjs";
import { useDispatch, useSelector } from 'react-redux';
import CommonHeader from '../../components/HeaderComponent/CommonHeader';
import { useNavigate } from 'react-router-dom';
import PoolHistoryDetailsModal from "../../components/Pooling/PoolHistoryDetailsModal";
import UploadDocumentsModal from "./UploadDocumentModal";

const PoolingHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { poolingHistoryData, loading, poolingHistoryPaginationData } = useSelector((state) => state.pooling);
  const { user } = useSelector((state) => state.user);

  const [activeButton, setActiveButton] = useState("All Records");
  const [modalVisible, setModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortType, setSortType] = useState('newest');
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [poolSelectedToDelete, setPoolSelectedToDelete] = useState(null);
  const [poolStatus, setPoolStatus] = useState(null);
  const [poolType, setPoolType] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [companySummary, setCompanySummary] = useState({});
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolingHistoryList, setPoolingHistoryList] = useState([]);

  useEffect(() => {
    handlePageLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortType]);

  const columns = [
    {
      title: 'Pool ID',
      dataIndex: 'pool_unique_id',
      key: 'pool_unique_id',
    },
    {
      title: 'Date Created',
      dataIndex: 'date_created',
      key: 'date_created',
      render: (date) => dayjs(date).format('DD MMM YY, hh:mm A'),
    },
    {
      title: 'Pool Status',
      dataIndex: 'pool_status',
      key: 'pool_status',
    },
    {
      title: 'CB Consumed',
      dataIndex: ['close_cb', 'close_cb'],
      key: 'cb_consumed',
      render: (cb) => (cb / 1e6).toFixed(4),
    },
    {
      title: 'Vessels',
      dataIndex: 'vessels_count',
      key: 'vessels_count',
    },
    {
      title: 'Pool Type',
      dataIndex: 'pool_type',
      key: 'pool_type',
      render: (type) => (type ? type.charAt(0).toUpperCase() + type.slice(1) : ""),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button
            onClick={() =>
              handleOpenDetails(
                record.pool_id || record.pool_unique_id,  // ✅ FIXED
                record.pool_status,
                record.pool_type
              )
            }
          >
            Open Details
          </Button>

          {user.role === "Admin Pooling Agent" && (
            <Button onClick={() => ManualEdit(record.pool_unique_id)}>Edit</Button>
          )}

          <Button danger onClick={() => handleDeleteModalVisibility(record.pool_unique_id)}>
            Delete
          </Button>
        </>
      ),
    },
  ];

  const ManualEdit = (pool_id) => {
    if (user.role === "Admin Pooling Agent") {
      navigate("/pooling", {
        state: { data: { pool_id, from: "poolingHistory" } },
      });
    }
  };

 
  const handleOpenDetails = async (pool_id, pool_status, pool_type) => {
    console.log("OPEN DETAILS pool_id SENT:", pool_id);

    await dispatch(poolingHistoryOpenDetails({ pool_id })); // backend expects pool_id

    setPoolStatus(pool_status);
    setPoolType(pool_type);
    setModalVisible(true);
  };

  const handleDeleteModalVisibility = (pool_id) => {
    setDeleteConfirmationModal(true);
    setPoolSelectedToDelete(pool_id);
  };

  const handleDelete = async () => {
    const response = await dispatch(deletePool({ pool_id: poolSelectedToDelete }));
    if (response.meta.requestStatus === 'fulfilled') {
      setDeleteConfirmationModal(false);
      notification.success({
        message: "Pool Deleted Successfully",
        description: `Pool ID ${poolSelectedToDelete} deleted successfully.`,
        placement: "topRight",
      });
      handlePageLoad();
    } else {
      setDeleteConfirmationModal(false);
      notification.error({
        message: "Deletion Failed",
        description: "Error deleting the pool. Please try again.",
        placement: "topRight",
      });
    }
  };

  const handlePageLoad = async () => {
    const res = await dispatch(poolingHistory({ page: currentPage, sortType }));
    if (res.payload?.data) setPoolingHistoryList(res.payload.data);
  };

  const handleButtonClick = (buttonName) => {
    setActiveButton(buttonName);
    const sortMap = {
      "Newest first": "newest",
      "Oldest first": "oldest",
      "Approved pool": "approved",
      "Pending pool": "pending",
      "Declined pool": "declined",
    };
    setSortType(sortMap[buttonName] || 'newest');
  };

  const handlePaginationChange = (page) => setCurrentPage(page);

  return (
    <div className="main-page">
      <div className="page-contents">
        <CommonHeader
          headerKey="pool_overview"
          headerTitle="Pool Overview"
          type="pool_overview"
        />

        <div className="record-type-buttons">
          {["All Records", "Newest first", "Oldest first", "Approved pool", "Pending pool", "Declined pool"].map((btn) => (
            <Button
              key={btn}
              className={activeButton === btn ? "button-active" : "button-inactive"}
              style={{ fontSize: "1.8vh", padding: "2.5vh" }}
              onClick={() => handleButtonClick(btn)}
            >
              {btn}
            </Button>
          ))}
        </div>
      </div>

      <div className="scrollable-container">
        {loading ? (
          <Spin size="large" />
        ) : (
          <AntTable
            columns={columns}
            dataSource={poolingHistoryList}
            rowKey="pool_unique_id"
            pagination={false}
            expandable={{
              expandedRowRender: (poolRecord) => (
                <div style={{ background: "#f6f8ff", padding: 16 }}>
                  <AntTable
                    columns={[
                      { title: "Company Name", dataIndex: "company_name", key: "company_name" },
                      { title: "Surplus", dataIndex: "surplus", key: "surplus" },
                      { title: "Deficit", dataIndex: "deficit", key: "deficit" },
                      { title: "Vessel Count", dataIndex: "vessel_count", key: "vessel_count" },
                      { title: "Net Payment", dataIndex: "net_payment", key: "net_payment" },
                      {
                        title: "Documents",
                        dataIndex: "documents",
                        key: "documents",
                        render: (_, companyRow) => (
                          <Button
                            type="default"
                            onClick={() => {
                              setSelectedPool({
                                pool_unique_id: poolRecord.pool_unique_id,
                                company_name: companyRow.company_name,
                              });
                              setUploadModalVisible(true);
                            }}
                          >
                            Upload Docs
                          </Button>
                        ),
                      },
                    ]}
                    dataSource={Array.isArray(companySummary[poolRecord.pool_unique_id]) ? companySummary[poolRecord.pool_unique_id] : []}
                    rowKey="company_name"
                    pagination={false}
                    size="small"
                  />
                </div>
              ),
              expandedRowKeys,
              onExpandedRowsChange: (keys) =>
                setExpandedRowKeys(keys.length > 0 ? [keys[keys.length - 1]] : []),
              onExpand: async (expanded, record) => {
                if (expanded && !companySummary[record.pool_unique_id]) {
                  const res = await dispatch(fetchCompanySummary(record.pool_unique_id));
                  setCompanySummary(prev => ({
                    ...prev,
                    [record.pool_unique_id]: res.payload || [],
                  }));
                }
              },
            }}
          />
        )}
      </div>

      <div className="pagination-container">
        <Pagination
          current={poolingHistoryPaginationData.current_page}
          total={poolingHistoryPaginationData.total_items}
          pageSize={8}
          onChange={handlePaginationChange}
          showSizeChanger={false}
          showQuickJumper
        />
      </div>

      {modalVisible && (
        <PoolHistoryDetailsModal
          isVisible={modalVisible}
          poolStatus={poolStatus}
          poolType={poolType}
          onClose={() => setModalVisible(false)}
        />
      )}

      {uploadModalVisible && selectedPool && (
        <UploadDocumentsModal
          visible={uploadModalVisible}
          onCancel={() => setUploadModalVisible(false)}
          poolId={selectedPool.pool_unique_id}
          companyName={selectedPool.company_name}
        />
      )}

      {deleteConfirmationModal && (
        <Modal
          title="Confirm Deletion"
          open={deleteConfirmationModal}
          onOk={handleDelete}
          onCancel={() => setDeleteConfirmationModal(false)}
          okText="Delete"
          cancelText="Cancel"
        >
          <p>Are you sure you want to delete Pool ID: {poolSelectedToDelete}?</p>
        </Modal>
      )}
    </div>
  );
};

export default PoolingHistory;
